import React, { useState, useEffect, useMemo } from 'react';
import {
   Container,
   Box,
   Typography,
   Paper,
   Grid,
   Card,
   CardContent,
   CardMedia,
   CircularProgress,
   Alert,
   Tabs,
   Tab,
   Divider,
   Chip,
   Avatar,
   Badge,
   List,
   ListItem,
   ListItemAvatar,
   ListItemText,
   Button,
   IconButton,
   TextField,
   InputAdornment,
   Tooltip,
   Select,
   MenuItem,
   FormControl,
   useMediaQuery,
   useTheme,
} from '@mui/material';
import {
   People as PeopleIcon,
   Search as SearchIcon,
   FilterList as FilterIcon,
   Group as GroupIcon,
   BusinessCenter as WorkIcon,
   Home as HomeIcon,
   Favorite as LoveIcon,
   Person as PersonIcon,
   PersonAdd as PersonAddIcon,
   ViewModule as GridViewIcon,
   FormatListBulleted as ListViewIcon,
   Send as SendIcon,
   Edit as EditIcon,
   CheckCircle as CheckCircleIcon,
   Luggage as TripIcon,
   Cancel,
   Event,
   Info as InfoIcon,
   Chat as ChatIcon,
   TravelExplore as TravelIcon,
   Event as EventIcon,
   PhotoLibrary as AlbumIcon,
   Notes as NotesIcon,
   Save as SaveIcon,
   Link as LinkIcon,
   VideoCall as VideoCallIcon,
   QrCode as QrCodeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useFloatingChat } from '../../contexts/FloatingChatContext';
import {
   useResponsive,
   useResponsiveTypography,
   useResponsiveSpacing,
} from '../../utils/responsive';
import {
   collection,
   query,
   where,
   getDocs,
   doc,
   getDoc,
   orderBy,
   updateDoc,
   serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
   RelationshipType,
   relationshipLabels,
   relationshipCategories,
} from '../../types/Relationship';
import EditRelationshipDialog from './EditRelationshipDialog';
import NetworkInviteModal from '../../components/network/NetworkInviteModal';
import ParticipantProfileModal from '../../components/ParticipantProfileModal';
import OnlineIndicator from '../../components/OnlineIndicator/OnlineIndicator';
import VirtualCallSchedulerModal from '../../components/VirtualCallScheduler/VirtualCallSchedulerModal';
import onlineStatusService from '../../services/OnlineStatusService';

import { generateFallbackAvatar } from '../../utils/avatarUtils';
import { getAuthHeaders } from '../../utils/auth';
import {
   getNetworkRequests,
   acceptNetworkRequest,
   declineNetworkRequest,
   cancelNetworkRequest,
   updateNetworkConnection,
} from '../../services/networkService';
import { NetworkRequest } from '../../types/Relationship';

import axios from 'axios';

// Utility function to clip names if they exceed 20 characters
const clipName = (name: string, maxLength: number = 20): string => {
   if (name.length <= maxLength) return name;
   return name.substring(0, maxLength) + '...';
};

// Define an interface for the contact with relationship info
interface Contact {
   id: string;
   userId: string;
   displayName: string;
   email: string;
   photoURL?: string;
   relationship?: {
      type: RelationshipType;
      label?: string;
      notes?: string;
   };
   lastInteraction?: Date;
   tripCount?: number;
   // eventCount?: number;
   // eventTitles: string[];
   // photoCount?: number;
   status?: 'pending' | 'accepted' | 'declined' | 'saved';
   // direction?: 'incoming' | 'outgoing' | 'saved';
   connectionSource?: string;
   // connectionSourceName?: string;
   // New fields from the endpoint
   relationshipType?: string;
   relationshipLabel?: string;
   notes?: string;
   connectionType?: string;
   profileData?: {
      bio?: string;
      location?: string;
      interests?: string[];
      title?: string;
      companyName?: string;
      website?: string;
   };
   source?: string;
   sourceId?: string;
   createdAt?: Date;
}

// Add this interface near the top with your other interfaces
interface User {
   email: string;
   displayName?: string;
   photoURL?: string;
   [key: string]: any;
}

const PeoplePage: React.FC = () => {
   const { currentUser } = useUser();
   const navigate = useNavigate();
   const { openFloatingChatForContact } = useFloatingChat();

   // Add responsive hooks
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
   const { isMobile: responsiveIsMobile, deviceType } = useResponsive();
   const { fontSize, getFontSize } = useResponsiveTypography();
   const { spacing, getSpacing } = useResponsiveSpacing();

   // State variables
   const [contacts, setContacts] = useState<Contact[]>([]);
   const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
   const [initialLoad, setInitialLoad] = useState(true);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [activeTab, setActiveTab] = useState<string>('all');
   const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
   const [searchQuery, setSearchQuery] = useState('');
   const [sortBy, setSortBy] = useState<string>('name');
   const [selectedCount, setSelectedCount] = useState(0);

   // Selected contact state for edit dialog
   const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

   // Edit dialog state
   const [editDialogOpen, setEditDialogOpen] = useState(false);

   // Network invite modal state
   const [inviteModalOpen, setInviteModalOpen] = useState(false);

   // Profile modal state
   const [profileModalOpen, setProfileModalOpen] = useState(false);
   const [selectedProfileContact, setSelectedProfileContact] = useState<Contact | null>(null);

   // Virtual call scheduler modal state
   const [callSchedulerOpen, setCallSchedulerOpen] = useState(false);
   const [selectedCallContact, setSelectedCallContact] = useState<Contact | null>(null);

   // Network requests state
   const [networkRequests, setNetworkRequests] = useState<NetworkRequest[]>([]);
   const [loadingNetworkRequests, setLoadingNetworkRequests] = useState(false);
   const [networkRequestError, setNetworkRequestError] = useState<string | null>(null);
   const [processingRequestId, setProcessingRequestId] = useState<string | null>(null);

   // Success state
   const [success, setSuccess] = useState<string | null>(null);

   // Notes management state
   const [editingNotes, setEditingNotes] = useState<{ [contactId: string]: boolean }>({});
   const [notesValues, setNotesValues] = useState<{ [contactId: string]: string }>({});
   const [savingNotes, setSavingNotes] = useState<{ [contactId: string]: boolean }>({});

   // Pending invitations state
   const [pendingInvitations, setPendingInvitations] = useState<NetworkRequest[]>([]);
   const [loadingPendingInvitations, setLoadingPendingInvitations] = useState(false);
   const [processingInvitationId, setProcessingInvitationId] = useState<string | null>(null);
   const [processingAction, setProcessingAction] = useState<'resend' | 'cancel' | null>(null);

   // Contact waiting state
   const [contactWaitingList, setContactWaitingList] = useState<any[]>([]);
   const [loadingContactWaiting, setLoadingContactWaiting] = useState(false);
   const [processingWaitingId, setProcessingWaitingId] = useState<string | null>(null);

   // Fetch contacts data from trip invitations, event invitations and relationships
   useEffect(() => {
      if (!currentUser) return;

      fetchContacts();
   }, [currentUser]);

   // Fetch network requests when component mounts
   useEffect(() => {
      console.log('🔄 useEffect triggered, currentUser:', currentUser?.uid);
      if (currentUser) {
         console.log(
            '📞 Calling fetchNetworkRequests, fetchPendingInvitations, and fetchContactWaiting'
         );
         fetchNetworkRequests();
         fetchPendingInvitations();
         fetchContactWaiting();
      }
   }, [currentUser]);

   // Fetch contacts from relationship_network collection
   const fetchContacts = async () => {
      if (!currentUser) return;

      setLoading(true);
      setError(null);

      try {
         const headers = await getAuthHeaders();
         const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/people/contacts?userId=${currentUser.uid}`,
            { headers }
         );

         if (response.data.success) {
            const enrichedContacts = response.data.data.map((contact: any, index: number) => ({
               id: contact.userId || contact.email || `contact_${index}`,
               userId: contact.userId || '',
               displayName: contact.displayName || contact.name || 'Unknown',
               email: contact.email || '',
               photoURL: contact.photoURL || '',
               relationshipType: contact.relationshipType,
               relationshipLabel: contact.relationshipLabel,
               notes: contact.notes,
               connectionType: contact.connectionType || 'relationship',
               profileData: contact.profileData || {},
               connectionSource: contact.source || 'relationship',
               status: contact.status || 'accepted',
               lastInteraction: contact.updatedAt ? new Date(contact.updatedAt) : new Date(),
               createdAt: contact.createdAt ? new Date(contact.createdAt) : new Date(),
               source: contact.source,
               sourceId: contact.sourceId,
            }));

            setContacts(enrichedContacts);
            setFilteredContacts(enrichedContacts);
            console.log(`Loaded ${enrichedContacts.length} contacts from relationship_network`);
            console.log('Sample contact data:', enrichedContacts[0]);
         } else {
            throw new Error(response.data.error || 'Failed to fetch contacts');
         }
         setInitialLoad(false);
      } catch (err: any) {
         console.error('Error fetching contacts from relationship_network:', err);
         setError(
            err.response?.data?.error ||
               err.message ||
               'Failed to load your network. Please try again.'
         );
      } finally {
         setLoading(false);
      }
   };

   // Fetch contact waiting list
   const fetchContactWaiting = async () => {
      if (!currentUser) return;

      setLoadingContactWaiting(true);
      try {
         const headers = await getAuthHeaders();
         const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/contact-waiting/list?userId=${currentUser.uid}`,
            { headers }
         );

         if (response.data.success) {
            setContactWaitingList(response.data.data || []);
            console.log(
               `✅ Loaded ${response.data.data?.length || 0} contacts waiting for approval`
            );
         }
      } catch (err: any) {
         console.error('Error fetching contact waiting list:', err);
      } finally {
         setLoadingContactWaiting(false);
      }
   };

   // Approve a contact from waiting list
   const handleApproveContact = async (waitingId: string) => {
      if (!currentUser) return;

      setProcessingWaitingId(waitingId);
      try {
         const headers = await getAuthHeaders();
         const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/contact-waiting/approve`,
            {
               userId: currentUser.uid,
               waitingId,
               relationshipType: 'friend',
               relationshipLabel: 'Friend',
            },
            { headers }
         );

         if (response.data.success) {
            setSuccess('Contact approved and added to your network!');
            // Refresh lists
            fetchContactWaiting();
            fetchContacts();
         }
      } catch (err: any) {
         console.error('Error approving contact:', err);
         setError(err.response?.data?.error || 'Failed to approve contact');
      } finally {
         setProcessingWaitingId(null);
         setTimeout(() => setSuccess(null), 3000);
      }
   };

   // Decline a contact from waiting list
   const handleDeclineContact = async (waitingId: string) => {
      if (!currentUser) return;

      setProcessingWaitingId(waitingId);
      try {
         const headers = await getAuthHeaders();
         const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/contact-waiting/reject`,
            {
               userId: currentUser.uid,
               waitingId,
            },
            { headers }
         );

         if (response.data.success) {
            setSuccess('Contact declined');
            // Refresh list
            fetchContactWaiting();
         }
      } catch (err: any) {
         console.error('Error declining contact:', err);
         setError(err.response?.data?.error || 'Failed to decline contact');
      } finally {
         setProcessingWaitingId(null);
         setTimeout(() => setSuccess(null), 3000);
      }
   };

   // Check if a network request has been sent to this contact
   const hasNetworkRequestPending = (userId: string, email: string) => {
      return networkRequests.some(
         (r) =>
            (r.toUserId === userId ||
               r.toUserEmail === email ||
               r.fromUserId === userId ||
               r.fromUserEmail === email) &&
            r.status === 'pending'
      );
   };

   // Check if a contact is already in my network
   const isInNetwork = (userId: string, email: string) => {
      return networkRequests.some(
         (r) =>
            (r.toUserId === userId ||
               r.toUserEmail === email ||
               r.fromUserId === userId ||
               r.fromUserEmail === email) &&
            r.status === 'accepted'
      );
   };

   // Handle network request sent
   const handleNetworkRequestSent = () => {
      fetchNetworkRequests();
   };

   const handleOpenProfileModal = (contact: Contact) => {
      setSelectedProfileContact(contact);
      setProfileModalOpen(true);
   };

   const handleCloseProfileModal = () => {
      setProfileModalOpen(false);
      setSelectedProfileContact(null);
   };

   useEffect(() => {
      fetchContacts();
   }, [editDialogOpen]);

   // Filter and sort contacts based on active tab, search query, and sort option
   const updateFilteredContacts = () => {
      const filtered = contacts.filter((contact) => {
         // Apply tab filter
         if (activeTab !== 'all') {
            // Get relationship type from either old or new data structure
            const relationshipType = contact.relationship?.type || contact.relationshipType;

            if (!relationshipType) return false;

            // Debug log to understand the data structure
            console.log(
               'Filtering contact:',
               contact.displayName,
               'relationshipType:',
               relationshipType,
               'activeTab:',
               activeTab
            );

            // Check if the relationship type is in the selected category
            const matchesCategory = relationshipCategories[
               activeTab as keyof typeof relationshipCategories
            ]?.includes(relationshipType as RelationshipType);

            return matchesCategory;
         }

         // Apply search filter
         if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
               contact.displayName.toLowerCase().includes(query) ||
               contact.email.toLowerCase().includes(query) ||
               (contact.profileData?.companyName &&
                  contact.profileData.companyName.toLowerCase().includes(query))
            );
         }

         return true;
      });

      // Apply sorting - only name sorting since we removed other options
      const sorted = filtered.sort((a, b) => {
         return a.displayName.localeCompare(b.displayName);
      });

      console.log('Filtered contacts:', filtered.length, 'from total:', contacts.length);
      setFilteredContacts(sorted);
   };

   // Update filtered contacts when contacts, search query, active tab, or sort option changes
   useEffect(() => {
      updateFilteredContacts();
   }, [contacts, searchQuery, activeTab, sortBy]);

   // Group contacts by relationship category for display
   const getContactsByCategory = () => {
      const grouped: Record<string, Contact[]> = {
         Family: [],
         Friends: [],
         Work: [],
         Romantic: [],
         Other: [],
         Uncategorized: [],
      };

      filteredContacts.forEach((contact) => {
         // Get relationship type from either old or new data structure
         const relationshipType = contact.relationship?.type || contact.relationshipType;

         if (!relationshipType) {
            grouped['Uncategorized'].push(contact);
            return;
         }

         // Find which category the relationship type belongs to
         let category = 'Uncategorized';

         for (const [cat, types] of Object.entries(relationshipCategories)) {
            if (types.includes(relationshipType as RelationshipType)) {
               category = cat;
               break;
            }
         }

         grouped[category].push(contact);
      });

      return grouped;
   };

   // Handle tab change
   const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
      setActiveTab(newValue);
   };

   // Handle search input change
   const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
   };

   // Add helper function to get relationship category icon
   const getRelationshipIcon = (relationshipType: RelationshipType) => {
      if (!relationshipType) return <PersonIcon />;

      // Find the category for this relationship type
      for (const [category, types] of Object.entries(relationshipCategories)) {
         if (types.includes(relationshipType)) {
            switch (category) {
               case 'Family':
                  return <HomeIcon />;
               case 'Friends':
                  return <GroupIcon />;
               case 'Work':
                  return <WorkIcon />;
               case 'Romantic':
                  return <LoveIcon />;
               default:
                  return <PersonIcon />;
            }
         }
      }

      return <PersonIcon />;
   };

   // Render relationship info
   const renderRelationshipInfo = (contact: Contact) => {
      return (
         <Box sx={{ mt: isMobile ? 0.5 : 1 }}>
            {/* Show relationship type and label */}
            {contact.relationshipType && (
               <Box
                  sx={{
                     display: 'flex',
                     flexWrap: 'wrap',
                     gap: isMobile ? 0.25 : 0.5,
                     mt: isMobile ? 0.25 : 0.5,
                  }}
               >
                  <Chip
                     size={isMobile ? 'small' : 'small'}
                     icon={getRelationshipIcon(contact.relationshipType as RelationshipType)}
                     label={
                        contact.relationshipLabel ||
                        relationshipLabels[contact.relationshipType as RelationshipType] ||
                        contact.relationshipType
                     }
                     color='primary'
                     variant='outlined'
                     sx={{
                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                        height: isMobile ? '20px' : '24px',
                     }}
                  />
               </Box>
            )}

            {/* Show source information */}
            {contact.source && (
               <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{
                     mt: 0.5,
                     display: 'block',
                     fontSize: isMobile ? '0.65rem' : '0.75rem',
                  }}
               >
                  Connected via: {contact.source.replace('_', ' ')}
               </Typography>
            )}
         </Box>
      );
   };

   // Helper function to get relationship color based on category
   const getRelationshipColor = (relationshipType?: string) => {
      if (!relationshipType) return '#9ca3af'; // Gray for unknown

      // Find which category this relationship type belongs to
      for (const [category, types] of Object.entries(relationshipCategories)) {
         if (types.includes(relationshipType as RelationshipType)) {
            switch (category) {
               case 'Work':
                  return '#81C784'; // Green
               case 'Friends':
                  return '#64B5F6'; // Blue
               case 'Family':
                  return '#E57373'; // Red
               case 'Romantic':
                  return '#F06292'; // Pink
               case 'Other':
                  return '#FFD54F'; // Yellow
               default:
                  return '#9ca3af'; // Gray
            }
         }
      }

      return '#9ca3af'; // Gray for unknown
   };

   // Helper function to get activity level
   const getActivityLevel = (contact: Contact) => {
      // This is a placeholder - you can implement actual activity logic
      const random = Math.random();
      if (random > 0.7) return 'high';
      if (random > 0.4) return 'medium';
      return 'low';
   };

   // Helper function to get activity color
   const getActivityColor = (level: string) => {
      switch (level) {
         case 'high':
            return '#10b981';
         case 'medium':
            return '#f59e0b';
         case 'low':
            return '#6b7280';
         default:
            return '#6b7280';
      }
   };

   // Render contact card for grid view
   const renderContactCard = (contact: Contact) => {
      const isConnected = isInNetwork(contact.userId, contact.email);
      const relationshipType = contact.relationship?.type || contact.relationshipType;
      const relationshipColor = getRelationshipColor(relationshipType);
      const activityLevel = getActivityLevel(contact);
      const activityColor = getActivityColor(activityLevel);

      return (
         <Card
            sx={{
               height: '100%',
               minHeight: isMobile ? '320px' : '360px',
               display: 'flex',
               flexDirection: 'column',
               transition: 'all 0.2s ease-in-out',
               cursor: 'pointer',
               borderRadius: '0.75rem',
               borderLeft: `4px solid ${relationshipColor}`,
               '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
               },
            }}
            onClick={() => handleOpenProfileModal(contact)}
         >
            <CardContent
               sx={{
                  flexGrow: 1,
                  p: isMobile ? 1.5 : 2,
               }}
            >
               {/* Header Section with Avatar and Info */}
               <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                  <Badge
                     overlap='circular'
                     anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                     badgeContent={
                        contact.userId ? (
                           <OnlineIndicator userId={contact.userId} size='medium' />
                        ) : null
                     }
                  >
                     <Avatar
                        src={contact.photoURL || undefined}
                        alt={contact.displayName}
                        sx={{
                           width: isMobile ? 45 : 55,
                           height: isMobile ? 45 : 55,
                           fontSize: isMobile ? '1.1rem' : '1.3rem',
                           fontWeight: 'bold',
                           backgroundColor: relationshipColor,
                           color: 'white',
                        }}
                     >
                        {contact.displayName?.charAt(0)?.toUpperCase() || 'U'}
                     </Avatar>
                  </Badge>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                     {/* Name */}
                     <Tooltip
                        title={contact.displayName.length > 15 ? contact.displayName : ''}
                        placement='top'
                     >
                        <Typography
                           variant='h6'
                           noWrap
                           onClick={
                              contact.displayName.length > 15
                                 ? (e) => {
                                      e.stopPropagation();
                                      alert(contact.displayName);
                                   }
                                 : undefined
                           }
                           sx={{
                              fontSize: isMobile ? '1rem' : '1.1rem',
                              cursor: contact.displayName.length > 15 ? 'pointer' : 'default',
                              fontWeight: 600,
                              mb: 0.5,
                              '&:hover':
                                 contact.displayName.length > 15
                                    ? {
                                         textDecoration: 'underline',
                                      }
                                    : {},
                           }}
                        >
                           {clipName(contact.displayName, 15)}
                        </Typography>
                     </Tooltip>

                     {/* Title and Company in column layout */}
                     {(contact.profileData?.title || contact.profileData?.companyName) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 0.5 }}>
                           {contact.profileData?.title && (
                              <Tooltip
                                 title={
                                    contact.profileData?.title &&
                                    contact.profileData.title.length > 20
                                       ? contact.profileData.title
                                       : ''
                                 }
                                 placement='top'
                              >
                                 <Typography
                                    variant='body2'
                                    noWrap
                                    onClick={
                                       contact.profileData?.title &&
                                       contact.profileData.title.length > 20
                                          ? (e) => {
                                               e.stopPropagation();
                                               alert(contact.profileData?.title || '');
                                            }
                                          : undefined
                                    }
                                    sx={{
                                       fontSize: isMobile ? '0.8rem' : '0.85rem',
                                       color: '#374151',
                                       fontWeight: 500,
                                       cursor:
                                          contact.profileData?.title &&
                                          contact.profileData.title.length > 20
                                             ? 'pointer'
                                             : 'default',
                                       '&:hover':
                                          contact.profileData?.title &&
                                          contact.profileData.title.length > 20
                                             ? {
                                                  textDecoration: 'underline',
                                               }
                                             : {},
                                    }}
                                 >
                                    {contact.profileData?.title &&
                                    contact.profileData.title.length > 20
                                       ? contact.profileData.title.substring(0, 20) + '...'
                                       : contact.profileData?.title}
                                 </Typography>
                              </Tooltip>
                           )}
                           {contact.profileData?.companyName && (
                              <Tooltip
                                 title={
                                    contact.profileData?.companyName &&
                                    contact.profileData.companyName.length > 20
                                       ? contact.profileData.companyName
                                       : ''
                                 }
                                 placement='top'
                              >
                                 <Typography
                                    variant='body2'
                                    noWrap
                                    onClick={
                                       contact.profileData?.companyName &&
                                       contact.profileData.companyName.length > 20
                                          ? (e) => {
                                               e.stopPropagation();
                                               alert(contact.profileData?.companyName || '');
                                            }
                                          : undefined
                                    }
                                    sx={{
                                       fontSize: isMobile ? '0.75rem' : '0.8rem',
                                       color: '#6b7280',
                                       fontWeight: 400,
                                       cursor:
                                          contact.profileData?.companyName &&
                                          contact.profileData.companyName.length > 20
                                             ? 'pointer'
                                             : 'default',
                                       '&:hover':
                                          contact.profileData?.companyName &&
                                          contact.profileData.companyName.length > 20
                                             ? {
                                                  textDecoration: 'underline',
                                               }
                                             : {},
                                    }}
                                 >
                                    {contact.profileData?.companyName &&
                                    contact.profileData.companyName.length > 20
                                       ? contact.profileData.companyName.substring(0, 20) + '...'
                                       : contact.profileData?.companyName}
                                 </Typography>
                              </Tooltip>
                           )}
                        </Box>
                     )}

                     {/* Status and Relationship */}
                     {contact.relationshipType && (
                        <Typography
                           variant='body2'
                           sx={{
                              fontSize: '0.75rem',
                              color: '#6b7280',
                              textTransform: 'capitalize',
                           }}
                        >
                           {contact.relationshipType}
                        </Typography>
                     )}
                  </Box>
               </Box>

               {/* Contact Activity Section */}
               <Box
                  sx={{
                     backgroundColor: '#f8fafc',
                     padding: 1.5,
                     borderRadius: 1,
                     mb: 2,
                     fontSize: '0.85rem',
                     color: '#475569',
                  }}
               >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                     <Typography
                        sx={{
                           fontSize: '0.85rem',
                           wordWrap: 'break-word',
                           wordBreak: 'break-word',
                           whiteSpace: 'pre-wrap',
                           flex: 1,
                        }}
                     >
                        {contact.notes && contact.notes.trim()
                           ? contact.notes
                           : 'Note preview not available'}
                     </Typography>
                  </Box>
                  {contact.tripCount && contact.tripCount > 0 && (
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '1rem' }}>🤝</Typography>
                        <Typography sx={{ fontSize: '0.85rem' }}>
                           Shared {contact.tripCount} trip{contact.tripCount > 1 ? 's' : ''}
                        </Typography>
                     </Box>
                  )}
               </Box>

               {/* Connection Strength */}
               <Box sx={{ display: 'flex', gap: 0.25, mb: 2 }}>
                  {[1, 2, 3, 4, 5].map((dot) => (
                     <Box
                        key={dot}
                        sx={{
                           width: 6,
                           height: 6,
                           borderRadius: '50%',
                           backgroundColor:
                              dot <= (contact.tripCount || 0) + 2 ? '#3b82f6' : '#e5e7eb',
                        }}
                     />
                  ))}
               </Box>

               {/* Notes Section */}
               <Box sx={{ mb: 2 }}>
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                     }}
                  >
                     <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ fontWeight: 'medium', fontSize: '0.9rem' }}
                     >
                        <NotesIcon sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                        Notes
                     </Typography>
                     {!editingNotes[contact.id] && (
                        <Tooltip title='Add/Edit Notes'>
                           <IconButton
                              size='small'
                              onClick={(e) => {
                                 e.stopPropagation();
                                 handleEditNotes(contact.id, contact.notes || '');
                              }}
                              sx={{ p: 0.5 }}
                           >
                              <EditIcon fontSize='small' />
                           </IconButton>
                        </Tooltip>
                     )}
                  </Box>

                  {editingNotes[contact.id] ? (
                     <Box>
                        <TextField
                           fullWidth
                           multiline
                           rows={2}
                           size='small'
                           placeholder='Add notes about this contact...'
                           value={notesValues[contact.id] || ''}
                           onChange={(e) =>
                              setNotesValues((prev) => ({ ...prev, [contact.id]: e.target.value }))
                           }
                           onClick={(e) => e.stopPropagation()}
                           sx={{ mb: 1 }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                           <Button
                              size='small'
                              onClick={(e) => {
                                 e.stopPropagation();
                                 handleCancelEditNotes(contact.id);
                              }}
                              disabled={savingNotes[contact.id]}
                           >
                              Cancel
                           </Button>
                           <Button
                              size='small'
                              variant='contained'
                              onClick={(e) => {
                                 e.stopPropagation();
                                 handleSaveNotes(contact.id);
                              }}
                              disabled={savingNotes[contact.id]}
                              startIcon={
                                 savingNotes[contact.id] ? (
                                    <CircularProgress size={16} />
                                 ) : (
                                    <SaveIcon />
                                 )
                              }
                           >
                              {savingNotes[contact.id] ? 'Saving...' : 'Save'}
                           </Button>
                        </Box>
                     </Box>
                  ) : null}
               </Box>

               {/* Quick Actions */}
               <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                  <Button
                     size='small'
                     startIcon={<ChatIcon />}
                     onClick={(e) => {
                        e.stopPropagation();
                        handleOpenChat(contact);
                     }}
                     sx={{
                        backgroundColor: '#f1f5f9',
                        color: '#475569',
                        border: 'none',
                        borderRadius: '0.375rem',
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        padding: '0.5rem 0.75rem',
                        transition: 'all 0.2s',
                        '&:hover': {
                           backgroundColor: '#e2e8f0',
                           transform: 'translateY(-1px)',
                        },
                     }}
                  >
                     Message
                  </Button>
                  <Button
                     size='small'
                     startIcon={<VideoCallIcon />}
                     onClick={(e) => {
                        e.stopPropagation();
                        handleScheduleCall(contact);
                     }}
                     sx={{
                        backgroundColor: '#e0f2fe',
                        color: '#0277bd',
                        border: 'none',
                        borderRadius: '0.375rem',
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        padding: '0.5rem 0.75rem',
                        transition: 'all 0.2s',
                        '&:hover': {
                           backgroundColor: '#b3e5fc',
                           transform: 'translateY(-1px)',
                        },
                     }}
                  >
                     Schedule Call
                  </Button>
               </Box>

               {/* Edit Relationship Button */}
               <Box sx={{ mt: 2 }}>
                  <Button
                     variant='outlined'
                     size={isMobile ? 'medium' : 'small'}
                     startIcon={<EditIcon />}
                     onClick={(e) => {
                        e.stopPropagation();
                        setSelectedContact(contact);
                        setEditDialogOpen(true);
                     }}
                     fullWidth
                     sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                     }}
                  >
                     Edit Relationship
                  </Button>
               </Box>
            </CardContent>
         </Card>
      );
   };

   // Render contact list item for list view
   const renderContactListItem = (contact: Contact) => {
      const isConnected = isInNetwork(contact.userId, contact.email);

      return (
         <ListItem
            divider
            sx={{
               '&:hover': {
                  bgcolor: 'action.hover',
               },
               ...(isConnected && {
                  borderLeft: '4px solid',
                  borderColor: 'success.main',
               }),
               py: isMobile ? 2 : 3,
            }}
            secondaryAction={
               <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title='Chat'>
                     <IconButton
                        size={isMobile ? 'medium' : 'small'}
                        onClick={() => handleOpenChat(contact)}
                        sx={{
                           minHeight: isMobile ? '40px' : '32px',
                           minWidth: isMobile ? '40px' : '32px',
                        }}
                        color='primary'
                     >
                        <ChatIcon fontSize={isMobile ? 'medium' : 'small'} />
                     </IconButton>
                  </Tooltip>
                  <Tooltip title='Schedule Call'>
                     <IconButton
                        size={isMobile ? 'medium' : 'small'}
                        onClick={() => handleScheduleCall(contact)}
                        sx={{
                           minHeight: isMobile ? '40px' : '32px',
                           minWidth: isMobile ? '40px' : '32px',
                        }}
                        color='info'
                     >
                        <VideoCallIcon fontSize={isMobile ? 'medium' : 'small'} />
                     </IconButton>
                  </Tooltip>
                  <Tooltip title='Edit Relationship'>
                     <IconButton
                        size={isMobile ? 'medium' : 'small'}
                        onClick={() => {
                           setSelectedContact(contact);
                           setEditDialogOpen(true);
                        }}
                        sx={{
                           minHeight: isMobile ? '40px' : '32px',
                           minWidth: isMobile ? '40px' : '32px',
                        }}
                     >
                        <EditIcon fontSize={isMobile ? 'medium' : 'small'} />
                     </IconButton>
                  </Tooltip>
               </Box>
            }
         >
            <ListItemAvatar>
               <Avatar
                  src={contact.photoURL || undefined}
                  alt={contact.displayName}
                  sx={{
                     width: isMobile ? 56 : 72,
                     height: isMobile ? 56 : 72,
                     fontSize: isMobile ? '1.4rem' : '1.8rem',
                     fontWeight: 'bold',
                  }}
               >
                  {contact.photoURL ? (
                     contact.displayName.charAt(0).toUpperCase()
                  ) : (
                     <PersonIcon sx={{ fontSize: isMobile ? '1.8rem' : '2.4rem' }} />
                  )}
               </Avatar>
            </ListItemAvatar>
            <ListItemText
               primary={
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: isMobile ? 0.5 : 1,
                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                     }}
                  >
                     <Tooltip title={contact.displayName.length > 20 ? contact.displayName : ''}>
                        <Typography
                           variant='body1'
                           sx={{
                              fontSize: isMobile ? fontSize.body1 : fontSize.h6,
                              fontWeight: 'medium',
                              cursor: contact.displayName.length > 20 ? 'help' : 'default',
                           }}
                        >
                           {clipName(contact.displayName)}
                        </Typography>
                     </Tooltip>
                     {(contact.relationship || contact.relationshipType) && (
                        <Chip
                           size={isMobile ? 'small' : 'small'}
                           icon={getRelationshipIcon(
                              (contact.relationship?.type ||
                                 contact.relationshipType) as RelationshipType
                           )}
                           label={
                              contact.relationship?.label ||
                              contact.relationshipLabel ||
                              relationshipLabels[
                                 (contact.relationship?.type ||
                                    contact.relationshipType) as RelationshipType
                              ]
                           }
                           color='primary'
                           variant='outlined'
                           sx={{
                              fontSize: isMobile ? '0.7rem' : '0.75rem',
                              height: isMobile ? '20px' : '24px',
                           }}
                        />
                     )}
                     <Chip
                        size={isMobile ? 'small' : 'small'}
                        label={contact.status}
                        color={
                           contact.status === 'pending'
                              ? 'warning'
                              : contact.status === 'accepted'
                                ? 'success'
                                : contact.status === 'declined'
                                  ? 'error'
                                  : 'default'
                        }
                        variant='outlined'
                        sx={{
                           ml: isMobile ? 0.5 : 1,
                           fontSize: isMobile ? '0.7rem' : '0.75rem',
                           height: isMobile ? '20px' : '24px',
                        }}
                     />
                  </Box>
               }
               secondary={
                  <Box>
                     <Typography
                        variant='body2'
                        component='span'
                        sx={{
                           fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                        }}
                     >
                        {contact.email}
                     </Typography>

                     {/* Notes section for list view */}
                     <Box sx={{ mt: 1 }}>
                        <Box
                           sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 0.5,
                           }}
                        >
                           <Typography
                              variant='caption'
                              color='text.secondary'
                              sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center' }}
                           >
                              <NotesIcon sx={{ fontSize: '0.8rem', mr: 0.5 }} />
                              Notes
                           </Typography>
                           {!editingNotes[contact.id] && (
                              <IconButton
                                 size='small'
                                 onClick={() => handleEditNotes(contact.id, contact.notes || '')}
                                 sx={{ p: 0.25 }}
                              >
                                 <EditIcon sx={{ fontSize: '0.8rem' }} />
                              </IconButton>
                           )}
                        </Box>

                        {editingNotes[contact.id] ? (
                           <Box>
                              <TextField
                                 fullWidth
                                 multiline
                                 rows={2}
                                 size='small'
                                 placeholder='Add notes about this contact...'
                                 value={notesValues[contact.id] || ''}
                                 onChange={(e) =>
                                    setNotesValues((prev) => ({
                                       ...prev,
                                       [contact.id]: e.target.value,
                                    }))
                                 }
                                 sx={{ mb: 1 }}
                              />
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                 <Button
                                    size='small'
                                    onClick={() => handleCancelEditNotes(contact.id)}
                                    disabled={savingNotes[contact.id]}
                                 >
                                    Cancel
                                 </Button>
                                 <Button
                                    size='small'
                                    variant='contained'
                                    onClick={() => handleSaveNotes(contact.id)}
                                    disabled={savingNotes[contact.id]}
                                    startIcon={
                                       savingNotes[contact.id] ? (
                                          <CircularProgress size={16} />
                                       ) : (
                                          <SaveIcon />
                                       )
                                    }
                                 >
                                    {savingNotes[contact.id] ? 'Saving...' : 'Save'}
                                 </Button>
                              </Box>
                           </Box>
                        ) : (
                           <Box>
                              {contact.notes ? (
                                 <Typography
                                    variant='caption'
                                    sx={{
                                       bgcolor: 'grey.50',
                                       p: 1,
                                       borderRadius: 1,
                                       border: '1px solid',
                                       borderColor: 'grey.200',
                                       fontSize: isMobile ? '0.7rem' : '0.75rem',
                                       fontStyle: 'italic',
                                       color: 'text.secondary',
                                       whiteSpace: 'pre-wrap',
                                       wordBreak: 'break-word',
                                       display: 'block',
                                       mt: 0.5,
                                    }}
                                 >
                                    {contact.notes}
                                 </Typography>
                              ) : (
                                 <Typography
                                    variant='caption'
                                    sx={{
                                       color: 'text.disabled',
                                       fontStyle: 'italic',
                                       fontSize: isMobile ? '0.7rem' : '0.75rem',
                                       display: 'block',
                                       mt: 0.5,
                                    }}
                                 >
                                    No notes added yet.
                                 </Typography>
                              )}
                           </Box>
                        )}
                     </Box>
                  </Box>
               }
            />
         </ListItem>
      );
   };

   // Create a processed contact list with fallback avatar URLs
   const processedContacts = useMemo(() => {
      return filteredContacts
         .map((contact) => {
            if (!contact) return null;

            // If no photo URL exists, create a fallback avatar URL
            if (!contact.photoURL && contact.displayName) {
               return {
                  ...contact,
                  photoURL: generateFallbackAvatar(contact.displayName),
               };
            }

            return contact;
         })
         .filter(Boolean);
   }, [filteredContacts]);

   // Fetch network requests function - now fetches from relationship_network
   const fetchNetworkRequests = async () => {
      if (!currentUser) return;

      try {
         setLoadingNetworkRequests(true);
         setNetworkRequestError(null);

         // Fetch pending relationships from relationship_network collection
         const relationshipNetworkRef = collection(db, 'relationship_network');
         
         // Get pending relationships sent TO me (I am relationToUserId)
         const incomingQuery = query(
            relationshipNetworkRef,
            where('relationToUserId', '==', currentUser.uid),
            where('status', '==', 'pending')
         );
         
         // Get pending relationships sent BY me (I am userId)
         const outgoingQuery = query(
            relationshipNetworkRef,
            where('userId', '==', currentUser.uid),
            where('status', '==', 'pending')
         );
         
         const [incomingSnapshot, outgoingSnapshot] = await Promise.all([
            getDocs(incomingQuery),
            getDocs(outgoingQuery)
         ]);
         
         console.log('📊 Found', incomingSnapshot.size, 'incoming and', outgoingSnapshot.size, 'outgoing pending relationships');
         
         // Format the data to match NetworkRequest interface
         const requests: NetworkRequest[] = [];
         
         // Process incoming requests (they sent to me)
         // Need to fetch the sender's profile
         for (const docSnap of incomingSnapshot.docs) {
            const data = docSnap.data();
            const senderId = data.userId;
            
            // Fetch sender's user profile
            let senderName = data.displayName || 'Unknown';
            let senderEmail = data.email || '';
            let senderPhoto = data.photoURL || null;
            
            try {
               const senderUserDoc = await getDoc(doc(db, 'users', senderId));
               if (senderUserDoc.exists()) {
                  const senderData = senderUserDoc.data();
                  senderName = senderData.displayName || senderData.name || senderName;
                  senderEmail = senderData.email || senderEmail;
                  senderPhoto = senderData.photoURL || senderPhoto;
                  console.log('👤 Fetched sender profile:', { name: senderName, photo: senderPhoto });
               }
            } catch (err) {
               console.warn('Could not fetch sender profile:', senderId);
            }
            
            console.log('📥 Incoming request:', { 
               from: senderName, 
               to: currentUser.displayName,
               senderPhoto: senderPhoto 
            });
            
            requests.push({
               id: docSnap.id,
               fromUserId: senderId,
               fromUserName: senderName,
               fromUserEmail: senderEmail,
               fromUserPhoto: senderPhoto,
               toUserId: currentUser.uid,
               toUserName: currentUser.displayName || '',
               toUserEmail: currentUser.email || '',
               toUserPhoto: currentUser.photoURL || null,
               relationshipType: data.relationType,
               relationshipLabel: data.relationshipLabel,
               message: data.notes || null,
               status: 'pending',
               createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            });
         }
         
         // Process outgoing requests (I sent to them)
         // Need to fetch the recipient's profile
         for (const docSnap of outgoingSnapshot.docs) {
            const data = docSnap.data();
            const recipientId = data.relationToUserId;
            
            // Fetch recipient's user profile
            let recipientName = data.displayName || 'Unknown';
            let recipientEmail = data.email || '';
            let recipientPhoto = data.photoURL || null;
            
            try {
               const recipientUserDoc = await getDoc(doc(db, 'users', recipientId));
               if (recipientUserDoc.exists()) {
                  const recipientData = recipientUserDoc.data();
                  recipientName = recipientData.displayName || recipientData.name || recipientName;
                  recipientEmail = recipientData.email || recipientEmail;
                  recipientPhoto = recipientData.photoURL || recipientPhoto;
                  console.log('👤 Fetched recipient profile:', { name: recipientName, photo: recipientPhoto });
               }
            } catch (err) {
               console.warn('Could not fetch recipient profile:', recipientId);
            }
            
            console.log('📤 Outgoing request:', { 
               from: currentUser.displayName, 
               to: recipientName,
               recipientPhoto: recipientPhoto 
            });
            
            requests.push({
               id: docSnap.id,
               fromUserId: currentUser.uid,
               fromUserName: currentUser.displayName || '',
               fromUserEmail: currentUser.email || '',
               fromUserPhoto: currentUser.photoURL || null,
               toUserId: recipientId,
               toUserName: recipientName,
               toUserEmail: recipientEmail,
               toUserPhoto: recipientPhoto,
               relationshipType: data.relationType,
               relationshipLabel: data.relationshipLabel,
               message: data.notes || null,
               status: 'pending',
               createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            });
         }
         
         console.log('✅ Processed', requests.length, 'total pending relationships');
         setNetworkRequests(requests);
      } catch (error) {
         console.error('Error fetching network requests:', error);
         setNetworkRequestError('Failed to load network requests. Please try again.');
      } finally {
         setLoadingNetworkRequests(false);
      }
   };

   // Fetch pending invitations function
   const fetchPendingInvitations = async () => {
      console.log('🔍 fetchPendingInvitations called');

      if (!currentUser) {
         console.log('❌ No current user, returning');
         return;
      }

      console.log('👤 Current user:', currentUser.uid, currentUser.email);

      try {
         setLoadingPendingInvitations(true);
         console.log('⏳ Loading pending invitations...');

         const headers = await getAuthHeaders();
         console.log('🔑 Headers obtained');

         const apiUrl = `${process.env.REACT_APP_API_URL}/api/network/invitations/${currentUser.uid}`;
         console.log('🌐 API URL:', apiUrl);

         const response = await axios.get(apiUrl, { headers });
         console.log('📡 API Response:', response.data);

         if (response.data.success) {
            console.log('✅ API Success - All invitations:', response.data.invitations);
            console.log(`📊 Total invitations: ${response.data.invitations.length}`);

            // Filter to show ONLY INCOMING invitations (that others sent TO me) - pending only
            // Do NOT show invitations that I sent to others
            const incomingInvitations = response.data.invitations.filter((inv: any) => {
               const isReceived = inv.direction === 'received';
               const isPending = inv.status === 'pending';
               const isToMe = inv.toUserId === currentUser.uid || inv.toUserEmail === currentUser.email;
               const isNotFromMe = inv.fromUserId !== currentUser.uid;
               
               console.log(
                  `🔍 Invitation ${inv.id}:`,
                  `direction=${inv.direction},`,
                  `status=${inv.status},`,
                  `fromUserId=${inv.fromUserId},`,
                  `toUserId=${inv.toUserId},`,
                  `toUserEmail=${inv.toUserEmail},`,
                  `isReceived=${isReceived},`,
                  `isPending=${isPending},`,
                  `isToMe=${isToMe},`,
                  `isNotFromMe=${isNotFromMe},`,
                  `willShow=${isReceived && isPending && isToMe && isNotFromMe}`
               );
               
               return isReceived && isPending && isToMe && isNotFromMe;
            });

            console.log(`✅ Filtered incoming invitations: ${incomingInvitations.length}`, incomingInvitations);
            setPendingInvitations(incomingInvitations);
         } else {
            console.error('❌ Failed to fetch invitations:', response.data.error);
         }
      } catch (error: any) {
         console.error('💥 Error fetching pending invitations:', error);
         console.error('💥 Error details:', error.response?.data || error.message);
      } finally {
         setLoadingPendingInvitations(false);
         console.log('✅ fetchPendingInvitations completed');
      }
   };

   // Handle accepting a network request
   const handleAcceptRequest = async (requestId: string) => {
      if (!currentUser) return;

      try {
         setProcessingRequestId(`accept-${requestId}`);

         // Update the relationship status to 'accepted' in relationship_network
         const relationshipRef = doc(db, 'relationship_network', requestId);
         await updateDoc(relationshipRef, {
            status: 'accepted',
            updatedAt: serverTimestamp(),
         });

         console.log('✅ Relationship accepted:', requestId);

         // Refresh pending invitations to update the UI
         await fetchPendingInvitations();
         
         // Refresh network requests and contacts
         await fetchNetworkRequests();
         await fetchContacts();

         // Show success message
         setSuccess('Connection request accepted successfully!');
         setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
         console.error('Error accepting network request:', error);
         setNetworkRequestError('Failed to accept request. Please try again.');
      } finally {
         setProcessingRequestId(null);
      }
   };

   // Handle declining a network request
   const handleDeclineRequest = async (requestId: string) => {
      if (!currentUser) return;

      try {
         setProcessingRequestId(`decline-${requestId}`);

         // Update the relationship status to 'declined' in relationship_network
         const relationshipRef = doc(db, 'relationship_network', requestId);
         await updateDoc(relationshipRef, {
            status: 'declined',
            updatedAt: serverTimestamp(),
         });

         console.log('✅ Relationship declined:', requestId);

         // Refresh pending invitations to update the UI
         await fetchPendingInvitations();
         
         // Refresh network requests
         await fetchNetworkRequests();

         // Show success message
         setSuccess('Connection request declined');
         setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
         console.error('Error declining network request:', error);
         setNetworkRequestError('Failed to decline request. Please try again.');
      } finally {
         setProcessingRequestId(null);
      }
   };

   // Handle canceling a network request
   const handleCancelRequest = async (requestId: string) => {
      if (!currentUser) return;

      try {
         setProcessingRequestId(requestId);

         await cancelNetworkRequest(requestId, currentUser.uid);

         // Refresh network requests and pending invitations
         fetchNetworkRequests();
         fetchPendingInvitations();

         // Show success toast or message
      } catch (error) {
         console.error('Error canceling network request:', error);
         setNetworkRequestError('Failed to cancel request. Please try again.');
      } finally {
         setProcessingRequestId(null);
      }
   };

   // Handle resending a network request
   const handleResendNetworkRequest = async (requestId: string) => {
      if (!currentUser) return;

      try {
         setProcessingRequestId(requestId);

         const headers = await getAuthHeaders();
         const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/network/resend/${requestId}`,
            { userId: currentUser.uid },
            { headers }
         );

         if (response.data.success) {
            setSuccess('Network request resent successfully!');
            setTimeout(() => setSuccess(null), 3000);

            // Refresh network requests
            fetchNetworkRequests();
         } else {
            setNetworkRequestError('Failed to resend request. Please try again.');
         }
      } catch (error) {
         console.error('Error resending network request:', error);
         setNetworkRequestError('Failed to resend request. Please try again.');
      } finally {
         setProcessingRequestId(null);
      }
   };

   // Handle resending an invitation
   const handleResendInvitation = async (requestId: string) => {
      if (!currentUser) return;

      try {
         setProcessingInvitationId(requestId);
         setProcessingAction('resend');

         const headers = await getAuthHeaders();
         const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/network/resend/${requestId}`,
            { userId: currentUser.uid },
            { headers }
         );

         if (response.data.success) {
            const successMessage = response.data.emailSent
               ? 'Invitation resent successfully!'
               : 'Invitation exists but email could not be sent - please check email configuration';

            setSuccess(successMessage);
            setTimeout(() => setSuccess(null), 3000);

            // Refresh pending invitations to update last resent timestamp
            fetchPendingInvitations();
         } else {
            throw new Error(response.data.error || 'Failed to resend invitation');
         }
      } catch (error: any) {
         console.error('Error resending invitation:', error);
         setError(
            error.response?.data?.error ||
               error.message ||
               'Failed to resend invitation. Please try again.'
         );
         setTimeout(() => setError(null), 3000);
      } finally {
         setProcessingInvitationId(null);
         setProcessingAction(null);
      }
   };

   // Handle canceling an invitation
   const handleCancelInvitation = async (requestId: string) => {
      if (!currentUser) return;

      try {
         setProcessingInvitationId(requestId);
         setProcessingAction('cancel');

         const headers = await getAuthHeaders();
         const response = await axios.delete(
            `${process.env.REACT_APP_API_URL}/api/network/cancel/${requestId}`,
            {
               headers,
               data: { userId: currentUser.uid },
            }
         );

         if (response.data.success) {
            setSuccess('Invitation cancelled successfully!');
            setTimeout(() => setSuccess(null), 3000);

            // Refresh pending invitations to remove the cancelled invitation
            fetchPendingInvitations();
         } else {
            throw new Error(response.data.error || 'Failed to cancel invitation');
         }
      } catch (error: any) {
         console.error('Error cancelling invitation:', error);
         setError(
            error.response?.data?.error ||
               error.message ||
               'Failed to cancel invitation. Please try again.'
         );
         setTimeout(() => setError(null), 3000);
      } finally {
         setProcessingInvitationId(null);
         setProcessingAction(null);
      }
   };

   // Debug function to check email invitations
   const debugCheckEmail = async (email: string) => {
      if (!currentUser) return;

      try {
         const headers = await getAuthHeaders();
         const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/network/debug/check-email/${encodeURIComponent(email)}`,
            { headers }
         );

         if (response.data.success) {
            console.log(`Debug check for ${email}:`, response.data);
            alert(
               `Found ${response.data.pendingCount} pending invitations for ${email}. Check console for details.`
            );
         }
      } catch (error) {
         console.error('Error checking email:', error);
      }
   };

   // Render network requests section
   const renderNetworkRequests = () => {
      // Filter requests by direction and status
      // Incoming: requests sent TO me (where I'm the recipient)
      const incomingRequests = networkRequests.filter(
         (r) => 
            (r.toUserId === currentUser?.uid || r.toUserEmail === currentUser?.email) && 
            r.status === 'pending'
      );

      // Outgoing: requests sent BY me (where I'm the sender)
      const outgoingRequests = networkRequests.filter(
         (r) => r.fromUserId === currentUser?.uid && r.status === 'pending'
      );

      if (loadingNetworkRequests) {
         return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
               <CircularProgress size={24} />
            </Box>
         );
      }

      if (networkRequestError) {
         return (
            <Alert severity='error' sx={{ my: 2 }}>
               {networkRequestError}
            </Alert>
         );
      }

      if (incomingRequests.length === 0 && outgoingRequests.length === 0) {
         return (
            <Box sx={{ textAlign: 'center', py: 3 }}>
               <Typography variant='body2' color='text.secondary'>
                  No pending network requests
               </Typography>
               <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                  You can add people to your network from events you attend together
               </Typography>
            </Box>
         );
      }

      return (
         <Box>
            {incomingRequests.length > 0 && (
               <Box sx={{ mb: 3 }}>
                  <Typography variant='subtitle1' color='primary' fontWeight='bold' gutterBottom>
                     Incoming Requests ({incomingRequests.length})
                  </Typography>
                  <Paper variant='outlined' sx={{ p: 0 }}>
                     <List disablePadding>
                        {incomingRequests.map((request, index) => (
                           <ListItem
                              key={request.id}
                              divider={index < incomingRequests.length - 1}
                              secondaryAction={
                                 <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                       size='small'
                                       variant='outlined'
                                       color='inherit'
                                       onClick={() => handleDeclineRequest(request.id!)}
                                       disabled={processingRequestId === `accept-${request.id}` || processingRequestId === `decline-${request.id}`}
                                       startIcon={
                                          processingRequestId === `decline-${request.id}` ? (
                                             <CircularProgress size={16} color='inherit' />
                                          ) : undefined
                                       }
                                    >
                                       {processingRequestId === `decline-${request.id}`
                                          ? 'Declining...'
                                          : 'Decline'}
                                    </Button>
                                    <Button
                                       size='small'
                                       variant='contained'
                                       color='primary'
                                       onClick={() => handleAcceptRequest(request.id!)}
                                       disabled={processingRequestId === `accept-${request.id}` || processingRequestId === `decline-${request.id}`}
                                       startIcon={
                                          processingRequestId === `accept-${request.id}` ? (
                                             <CircularProgress size={16} color='inherit' />
                                          ) : null
                                       }
                                    >
                                       {processingRequestId === `accept-${request.id}`
                                          ? 'Accepting...'
                                          : 'Accept'}
                                    </Button>
                                 </Box>
                              }
                           >
                              <ListItemAvatar>
                                 <Avatar
                                    src={request.fromUserPhoto || undefined}
                                    sx={{
                                       width: 48,
                                       height: 48,
                                       fontSize: '1.1rem',
                                       fontWeight: 'bold',
                                    }}
                                 >
                                    {request.fromUserName.charAt(0).toUpperCase()}
                                 </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                 primary={
                                    <Typography variant='body1' fontWeight='medium'>
                                       <span style={{ color: '#01949A' }}>{request.fromUserName}</span>
                                       {' → You'}
                                    </Typography>
                                 }
                                 secondary={
                                    <>
                                       <Typography component='span' variant='body2'>
                                          From: {request.fromUserEmail}
                                       </Typography>
                                       {request.relationshipType && (
                                          <Chip
                                             size='small'
                                             label={relationshipLabels[request.relationshipType]}
                                             sx={{ ml: 1 }}
                                          />
                                       )}
                                       {request.eventTitle && (
                                          <Typography
                                             component='div'
                                             variant='caption'
                                             color='primary'
                                             sx={{ mt: 0.5 }}
                                          >
                                             <strong>Met at:</strong> {request.eventTitle}
                                          </Typography>
                                       )}
                                       {request.message && (
                                          <Typography
                                             component='div'
                                             variant='caption'
                                             sx={{ mt: 0.5, fontStyle: 'italic' }}
                                          >
                                             "{request.message}"
                                          </Typography>
                                       )}
                                       <Typography
                                          component='div'
                                          variant='caption'
                                          color='text.secondary'
                                          sx={{ mt: 0.5 }}
                                       >
                                          Received {new Date(request.createdAt).toLocaleDateString()}
                                       </Typography>
                                    </>
                                 }
                              />
                           </ListItem>
                        ))}
                     </List>
                  </Paper>
               </Box>
            )}

            {outgoingRequests.length > 0 && (
               <Box>
                  <Typography variant='subtitle1' color='secondary' fontWeight='bold' gutterBottom>
                     Sent Requests ({outgoingRequests.length})
                  </Typography>
                  <Paper variant='outlined' sx={{ p: 0 }}>
                     <List disablePadding>
                        {outgoingRequests.map((request, index) => (
                           <ListItem
                              key={request.id}
                              divider={index < outgoingRequests.length - 1}
                              secondaryAction={
                                 <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                       size='small'
                                       variant='outlined'
                                       color='primary'
                                       onClick={() => handleResendNetworkRequest(request.id!)}
                                       disabled={processingRequestId === request.id}
                                       startIcon={
                                          processingRequestId === request.id ? (
                                             <CircularProgress size={16} color='inherit' />
                                          ) : (
                                             <SendIcon />
                                          )
                                       }
                                    >
                                       {processingRequestId === request.id ? 'Resending...' : 'Resend'}
                                    </Button>
                                    <Button
                                       size='small'
                                       variant='outlined'
                                       color='error'
                                       onClick={() => handleCancelRequest(request.id!)}
                                       disabled={processingRequestId === request.id}
                                    >
                                       Cancel
                                    </Button>
                                 </Box>
                              }
                           >
                              <ListItemAvatar>
                                 <Avatar
                                    src={request.toUserPhoto || undefined}
                                    sx={{
                                       width: 48,
                                       height: 48,
                                       fontSize: '1.1rem',
                                       fontWeight: 'bold',
                                    }}
                                 >
                                    {(request.toUserName || request.toUserEmail.charAt(0))
                                       .charAt(0)
                                       .toUpperCase()}
                                 </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                 primary={
                                    <Typography variant='body1' fontWeight='medium'>
                                       {'You → '}
                                       <span style={{ color: '#01949A' }}>{request.toUserName || request.toUserEmail.split('@')[0]}</span>
                                    </Typography>
                                 }
                                 secondary={
                                    <>
                                       <Typography component='span' variant='body2'>
                                          To: {request.toUserEmail}
                                       </Typography>
                                       {request.relationshipType && (
                                          <Chip
                                             size='small'
                                             label={relationshipLabels[request.relationshipType]}
                                             sx={{ ml: 1 }}
                                          />
                                       )}
                                       {request.eventTitle && (
                                          <Typography
                                             component='div'
                                             variant='caption'
                                             color='secondary'
                                             sx={{ mt: 0.5 }}
                                          >
                                             <strong>Event:</strong> {request.eventTitle}
                                          </Typography>
                                       )}
                                       <Typography
                                          component='div'
                                          variant='caption'
                                          sx={{ mt: 0.5 }}
                                       >
                                          Sent {new Date(request.createdAt).toLocaleDateString()}
                                       </Typography>
                                    </>
                                 }
                              />
                           </ListItem>
                        ))}
                     </List>
                  </Paper>
               </Box>
            )}
         </Box>
      );
   };

   // Render pending invitations section
   const renderPendingInvitations = () => {
      if (loadingPendingInvitations) {
         return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
               <CircularProgress size={24} />
            </Box>
         );
      }

      if (pendingInvitations.length === 0) {
         return (
            <Box sx={{ textAlign: 'center', py: 3 }}>
               <Typography variant='body2' color='text.secondary'>
                  No sent invitations
               </Typography>
               <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                  Invitations you send will appear here with their current status
               </Typography>
            </Box>
         );
      }

      return (
         <Box>
            <Typography variant='subtitle1' color='primary' fontWeight='bold' gutterBottom>
               Sent Invitations ({pendingInvitations.length})
            </Typography>
            <Paper variant='outlined' sx={{ p: 0 }}>
               <List disablePadding>
                  {pendingInvitations.map((invitation, index) => (
                     <ListItem
                        key={invitation.id}
                        divider={index < pendingInvitations.length - 1}
                        secondaryAction={
                           invitation.status === 'pending' ? (
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                 <Button
                                    size='small'
                                    variant='outlined'
                                    color='primary'
                                    onClick={() => handleResendInvitation(invitation.id!)}
                                    disabled={processingInvitationId === invitation.id}
                                    startIcon={
                                       processingInvitationId === invitation.id ? (
                                          <CircularProgress size={16} color='inherit' />
                                       ) : (
                                          <SendIcon />
                                       )
                                    }
                                 >
                                    {processingInvitationId === invitation.id
                                       ? 'Resending...'
                                       : 'Resend'}
                                 </Button>
                                 <Button
                                    size='small'
                                    variant='outlined'
                                    color='error'
                                    onClick={() => handleCancelRequest(invitation.id!)}
                                    disabled={processingInvitationId === invitation.id}
                                 >
                                    Cancel
                                 </Button>
                              </Box>
                           ) : invitation.status === 'declined' ? (
                              <Chip
                                 size='small'
                                 label='Declined'
                                 color='error'
                                 variant='outlined'
                                 icon={<Cancel fontSize='small' />}
                              />
                           ) : null
                        }
                     >
                        <ListItemAvatar>
                           <Avatar
                              src={invitation.toUserPhoto || undefined}
                              sx={{
                                 width: 48,
                                 height: 48,
                                 fontSize: '1.1rem',
                                 fontWeight: 'bold',
                              }}
                           >
                              {(invitation.toUserName || invitation.toUserEmail.charAt(0))
                                 .charAt(0)
                                 .toUpperCase()}
                           </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                           primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                 <Typography variant='body1' fontWeight='medium'>
                                    {invitation.toUserName || invitation.toUserEmail.split('@')[0]}
                                 </Typography>
                                 <Chip
                                    size='small'
                                    label={
                                       invitation.status.charAt(0).toUpperCase() +
                                       invitation.status.slice(1)
                                    }
                                    color={
                                       invitation.status === 'pending'
                                          ? 'warning'
                                          : invitation.status === 'accepted'
                                            ? 'success'
                                            : invitation.status === 'declined'
                                              ? 'error'
                                              : 'default'
                                    }
                                    icon={
                                       invitation.status === 'pending' ? (
                                          <InfoIcon fontSize='small' />
                                       ) : invitation.status === 'accepted' ? (
                                          <CheckCircleIcon fontSize='small' />
                                       ) : invitation.status === 'declined' ? (
                                          <Cancel fontSize='small' />
                                       ) : undefined
                                    }
                                 />
                              </Box>
                           }
                           secondary={
                              <>
                                 <Typography
                                    component='span'
                                    variant='body2'
                                    color='text.secondary'
                                 >
                                    {invitation.toUserEmail}
                                 </Typography>
                                 {invitation.relationshipType && (
                                    <Chip
                                       size='small'
                                       label={relationshipLabels[invitation.relationshipType]}
                                       variant='outlined'
                                       sx={{ ml: 1 }}
                                    />
                                 )}
                                 <Typography
                                    component='div'
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{ mt: 0.5 }}
                                 >
                                    Sent {new Date(invitation.createdAt).toLocaleDateString()}
                                    {invitation.lastResent && (
                                       <span style={{ marginLeft: 8 }}>
                                          • Last resent{' '}
                                          {new Date(invitation.lastResent).toLocaleDateString()}
                                       </span>
                                    )}
                                 </Typography>
                                 {invitation.message && (
                                    <Typography
                                       component='div'
                                       variant='caption'
                                       color='text.secondary'
                                       sx={{
                                          mt: 0.5,
                                          fontStyle: 'italic',
                                          backgroundColor: 'grey.50',
                                          p: 0.5,
                                          borderRadius: 0.5,
                                       }}
                                    >
                                       "{invitation.message}"
                                    </Typography>
                                 )}
                              </>
                           }
                        />
                     </ListItem>
                  ))}
               </List>
            </Paper>
         </Box>
      );
   };

   // Render the main content
   const renderContent = () => {
      return (
         <Box>
            {initialLoad && loading ? (
               <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
               </Box>
            ) : error ? (
               <Alert severity='error' sx={{ my: 2 }}>
                  {error}
               </Alert>
            ) : (
               <>
                  {/* Always show contacts list */}
                  {renderContactsList()}
               </>
            )}
         </Box>
      );
   };

   // Render the main content based on the view mode
   const renderContactsList = () => {
      if (filteredContacts.length === 0) {
         return (
            <Paper
               sx={{
                  p: isMobile ? 2 : 4,
                  textAlign: 'center',
                  borderRadius: isMobile ? 1 : 2,
               }}
            >
               <Typography
                  variant='h6'
                  gutterBottom
                  sx={{
                     fontSize: isMobile ? fontSize.h6 : fontSize.h5,
                  }}
               >
                  No contacts found
               </Typography>
               <Typography
                  color='text.secondary'
                  paragraph
                  sx={{
                     fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                  }}
               >
                  {searchQuery
                     ? 'No contacts match your search criteria.'
                     : 'Start by inviting people to your trips and events!'}
               </Typography>
            </Paper>
         );
      }

      return (
         <Box sx={{ mb: isMobile ? 1.5 : 2.5, p: isMobile ? 0.5 : 1.5 }}>
            {viewMode === 'grid' ? (
               <Grid container spacing={isMobile ? 1.5 : 2}>
                  {filteredContacts.map((contact) => (
                     <Grid item xs={12} sm={6} md={4} lg={4} key={contact.id}>
                        {renderContactCard(contact)}
                     </Grid>
                  ))}
               </Grid>
            ) : (
               <List
                  sx={{
                     '& .MuiListItem-root': {
                        px: isMobile ? 2 : 3,
                     },
                  }}
               >
                  {filteredContacts.map((contact) => renderContactListItem(contact))}
               </List>
            )}
         </Box>
      );
   };

   // Status badges for network connections
   const renderStatusBadge = (contact: Contact) => {
      if (!contact.status) return null;

      return (
         <Chip
            size='small'
            label={contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
            color={
               contact.status === 'accepted'
                  ? 'success'
                  : contact.status === 'pending'
                    ? 'warning'
                    : contact.status === 'declined'
                      ? 'error'
                      : 'default'
            }
            icon={contact.status === 'pending' ? <InfoIcon fontSize='small' /> : undefined}
            sx={{ ml: 1 }}
         />
      );
   };

   // Connection source indicator
   const renderConnectionSource = (contact: Contact) => {
      if (!contact.connectionSource) return null;

      const sourceIcon =
         contact.connectionSource === 'direct' ? (
            <PersonIcon fontSize='small' />
         ) : contact.connectionSource === 'event' ? (
            <Event fontSize='small' />
         ) : contact.connectionSource === 'trip' ? (
            <TripIcon fontSize='small' />
         ) : undefined;

      const sourceLabel =
         contact.connectionSource === 'direct'
            ? 'Direct connection'
            : contact.connectionSource === 'event'
              ? 'Via event'
              : contact.connectionSource === 'trip'
                ? 'Via trip'
                : '';

      return (
         <Tooltip title={sourceLabel}>
            <Chip
               size='small'
               icon={sourceIcon}
               label={contact.connectionSource || ''}
               variant='outlined'
               color='info'
               sx={{ ml: 1 }}
            />
         </Tooltip>
      );
   };

   // Accept/Decline buttons for pending connections
   // const renderPendingActions = (contact: Contact) => {
   //    if (contact.status !== 'pending' || contact.direction !== 'incoming') {
   //       return null;
   //    }

   //    return (
   //       <Box sx={{ mt: 1 }}>
   //          <Button
   //             size='small'
   //             variant='contained'
   //             color='success'
   //             startIcon={<CheckCircleIcon />}
   //             onClick={() => handleAcceptConnection(contact.id)}
   //             sx={{ mr: 1 }}
   //          >
   //             Accept
   //          </Button>
   //          <Button
   //             size='small'
   //             variant='outlined'
   //             color='error'
   //             startIcon={<Cancel />}
   //             onClick={() => handleDeclineConnection(contact.id)}
   //          >
   //             Decline
   //          </Button>
   //       </Box>
   //    );
   // };

   // Handler functions for accept/decline actions
   const handleAcceptConnection = async (connectionId: string) => {
      try {
         setLoading(true);
         await updateNetworkConnection(connectionId, 'accepted');
         // Refresh your contact list
         fetchContacts();
         setSuccess('Connection accepted');
      } catch (error) {
         console.error('Error accepting connection:', error);
         setError('Failed to accept connection');
      } finally {
         setLoading(false);
      }
   };

   const handleDeclineConnection = async (connectionId: string) => {
      try {
         setLoading(true);
         await updateNetworkConnection(connectionId, 'declined');
         // Refresh your contact list
         fetchContacts();
         setSuccess('Connection declined');
      } catch (error) {
         console.error('Error declining connection:', error);
         setError('Failed to decline connection');
      } finally {
         setLoading(false);
      }
   };

   // Handle opening chat for a contact
   const handleOpenChat = async (contact: Contact) => {
      try {
         await openFloatingChatForContact({
            id: contact.id,
            userId: contact.userId,
            displayName: contact.displayName,
            email: contact.email,
            photoURL: contact.photoURL || undefined,
            source: contact.source,
         });
      } catch (error) {
         console.error('Error opening chat:', error);
         setError('Failed to open chat. Please try again.');
         setTimeout(() => setError(null), 3000);
      }
   };

   // Handle opening virtual call scheduler
   const handleScheduleCall = (contact: Contact) => {
      setSelectedCallContact(contact);
      setCallSchedulerOpen(true);
   };

   // Handle closing virtual call scheduler
   const handleCloseCallScheduler = () => {
      setCallSchedulerOpen(false);
      setSelectedCallContact(null);
   };

   // Handle notes editing
   const handleEditNotes = (contactId: string, currentNotes: string = '') => {
      setEditingNotes((prev) => ({ ...prev, [contactId]: true }));
      setNotesValues((prev) => ({ ...prev, [contactId]: currentNotes }));
   };

   const handleCancelEditNotes = (contactId: string) => {
      setEditingNotes((prev) => ({ ...prev, [contactId]: false }));
      setNotesValues((prev) => ({ ...prev, [contactId]: '' }));
   };

   const handleSaveNotes = async (contactId: string) => {
      if (!currentUser) return;

      try {
         setSavingNotes((prev) => ({ ...prev, [contactId]: true }));

         const headers = await getAuthHeaders();
         const response = await axios.put(
            `${process.env.REACT_APP_API_URL}/api/people/contacts/${contactId}/notes`,
            {
               userId: currentUser.uid,
               notes: notesValues[contactId] || '',
            },
            { headers }
         );

         if (response.data.success) {
            // Update the contact in the local state
            setContacts((prev) =>
               prev.map((contact) =>
                  contact.id === contactId
                     ? { ...contact, notes: notesValues[contactId] || '' }
                     : contact
               )
            );
            setFilteredContacts((prev) =>
               prev.map((contact) =>
                  contact.id === contactId
                     ? { ...contact, notes: notesValues[contactId] || '' }
                     : contact
               )
            );

            setEditingNotes((prev) => ({ ...prev, [contactId]: false }));
            setSuccess('Notes saved successfully!');
            setTimeout(() => setSuccess(null), 3000);
         } else {
            throw new Error(response.data.error || 'Failed to save notes');
         }
      } catch (error: any) {
         console.error('Error saving notes:', error);
         setError(
            error.response?.data?.error ||
               error.message ||
               'Failed to save notes. Please try again.'
         );
         setTimeout(() => setError(null), 3000);
      } finally {
         setSavingNotes((prev) => ({ ...prev, [contactId]: false }));
      }
   };

   return (
      <Container
         maxWidth='xl'
         sx={{
            mt: isMobile ? 2 : 4,
            px: isMobile ? 0.5 : 1.5,
            width: '100%',
            overflow: 'hidden',
         }}
      >
         {/* Header Stats Section */}
         <Box
            sx={{
               background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
               color: 'white',
               p: isMobile ? 2 : 3,
               mb: 0,
            }}
         >
            <Box
               sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: isMobile ? 1 : 2,
               }}
            >
               <Typography
                  variant='h4'
                  sx={{
                     fontWeight: 'bold',
                     fontSize: isMobile ? '1.5rem' : '2rem',
                  }}
               >
                  My Network
               </Typography>
               <Box
                  sx={{
                     display: 'flex',
                     gap: isMobile ? 1.5 : 2,
                     flexWrap: 'wrap',
                     alignItems: 'center',
                  }}
               >
                  <Box sx={{ textAlign: 'center' }}>
                     <Typography
                        variant='h4'
                        sx={{
                           fontWeight: 'bold',
                           fontSize: isMobile ? '1.25rem' : '1.5rem',
                        }}
                     >
                        {filteredContacts.length}
                     </Typography>
                     <Typography
                        variant='body2'
                        sx={{
                           opacity: 0.9,
                           fontSize: isMobile ? '0.75rem' : '0.9rem',
                        }}
                     >
                        Total Contacts
                     </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                     <Typography
                        variant='h4'
                        sx={{
                           fontWeight: 'bold',
                           fontSize: isMobile ? '1.25rem' : '1.5rem',
                        }}
                     >
                        {networkRequests.filter(r => r.status === 'pending').length}
                     </Typography>
                     <Typography
                        variant='body2'
                        sx={{
                           opacity: 0.9,
                           fontSize: isMobile ? '0.75rem' : '0.9rem',
                        }}
                     >
                        Pending
                     </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                     <Typography
                        variant='h4'
                        sx={{
                           fontWeight: 'bold',
                           fontSize: isMobile ? '1.25rem' : '1.5rem',
                        }}
                     >
                        {
                           filteredContacts.filter(
                              (contact) =>
                                 contact.userId && onlineStatusService.isUserOnline(contact.userId)
                           ).length
                        }
                     </Typography>
                     <Typography
                        variant='body2'
                        sx={{
                           opacity: 0.9,
                           fontSize: isMobile ? '0.75rem' : '0.9rem',
                        }}
                     >
                        Online
                     </Typography>
                  </Box>
                  <Button
                     variant='contained'
                     startIcon={<PersonAddIcon />}
                     onClick={() => setInviteModalOpen(true)}
                     size={isMobile ? 'medium' : 'medium'}
                     sx={{
                        minHeight: isMobile ? '40px' : '44px',
                        px: isMobile ? 2 : 2.5,
                        fontSize: isMobile ? '0.875rem' : '0.9rem',
                        borderRadius: 2,
                        fontWeight: 500,
                        textTransform: 'none',
                        ml: isMobile ? 0 : 1,
                     }}
                  >
                     Invite to Network
                  </Button>
               </Box>
            </Box>
         </Box>

         {/* Controls Section */}
         {true && (
            <Paper
               sx={{
                  background: 'white',
                  borderBottom: '1px solid #e2e8f0',
                  p: 1.5,

                  borderRadius: 0,
                  mt: 0,
               }}
            >
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'center',
                     flexWrap: 'wrap',
                     gap: 1.5,
                  }}
               >
                  <Box
                     sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                     }}
                  >
                     <TextField
                        placeholder='Search contacts by name or company...'
                        size='small'
                        value={searchQuery}
                        onChange={handleSearchChange}
                        sx={{
                           width: isMobile ? '100%' : 250,
                           '& .MuiOutlinedInput-root': {
                              borderRadius: '0.5rem',
                              fontSize: '0.9rem',
                              backgroundColor: '#f9fafb',
                              border: '1px solid #d1d5db',
                           },
                        }}
                        InputProps={{
                           startAdornment: (
                              <InputAdornment position='start'>
                                 <SearchIcon />
                              </InputAdornment>
                           ),
                        }}
                     />

                     <FormControl size='small' sx={{ minWidth: 150 }}>
                        <Select
                           value={activeTab}
                           onChange={(e) => setActiveTab(e.target.value)}
                           displayEmpty
                           sx={{
                              borderRadius: '0.5rem',
                              fontSize: '0.9rem',
                              backgroundColor: '#f9fafb',
                              border: '1px solid #d1d5db',
                           }}
                        >
                           <MenuItem value='all'>All Relationships</MenuItem>
                           <MenuItem value='Work'>Work</MenuItem>
                           <MenuItem value='Friends'>Friends</MenuItem>
                           <MenuItem value='Family'>Family</MenuItem>
                           <MenuItem value='Romantic'>Romantic</MenuItem>
                           <MenuItem value='Other'>Other</MenuItem>
                        </Select>
                     </FormControl>

                     <FormControl size='small' sx={{ minWidth: 150 }}>
                        <Select
                           value={sortBy}
                           onChange={(e) => setSortBy(e.target.value)}
                           displayEmpty
                           sx={{
                              borderRadius: '0.5rem',
                              fontSize: '0.9rem',
                              backgroundColor: '#f9fafb',
                              border: '1px solid #d1d5db',
                           }}
                        >
                           <MenuItem value='name'>Sort by Name</MenuItem>
                        </Select>
                     </FormControl>
                  </Box>
               </Box>
            </Paper>
         )}

         {/* Legend Bar */}
         <Box
            sx={{
               background: 'white',
               padding: '0.75rem 1rem',
               borderBottom: '1px solid #e5e7eb',
               maxWidth: '900px',
               margin: '0 auto',
               mb: 2,
            }}
         >
            <Box
               sx={{
                  display: 'flex',
                  gap: '1rem',
                  alignItems: 'center',
                  fontSize: '0.85rem',
                  color: '#6b7280',
                  flexWrap: 'wrap',
                  maxWidth: '1200px',
                  margin: '0 auto',
               }}
            >
               <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <Box
                     sx={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '2px',
                        background: '#81C784',
                     }}
                  />
                  <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>Work</Typography>
               </Box>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <Box
                     sx={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '2px',
                        background: '#64B5F6',
                     }}
                  />
                  <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>Friends</Typography>
               </Box>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <Box
                     sx={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '2px',
                        background: '#E57373',
                     }}
                  />
                  <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>Family</Typography>
               </Box>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <Box
                     sx={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '2px',
                        background: '#F06292',
                     }}
                  />
                  <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>Romantic</Typography>
               </Box>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <Box
                     sx={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '2px',
                        background: '#FFD54F',
                     }}
                  />
                  <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>Other</Typography>
               </Box>
            </Box>
         </Box>

         {/* Pending Connections Section (Invitations + QR Contacts) */}
         {(networkRequests.filter(r => r.status === 'pending').length > 0 || contactWaitingList.length > 0) && (
            <Paper
               sx={{
                  background: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '0.75rem',
                  p: 1.5,
                  mb: 2,
               }}
            >
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'center',
                     mb: 1.5,
                  }}
               >
                  <Typography
                     variant='subtitle1'
                     sx={{
                        fontWeight: 500,
                        color: '#92400e',
                     }}
                  >
                     Pending Connections
                  </Typography>
                  <Box
                     sx={{
                        background: '#f59e0b',
                        color: 'white',
                        px: 1,
                        py: 0.5,
                        borderRadius: '9999px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                     }}
                  >
                     {networkRequests.filter(r => r.status === 'pending').length + contactWaitingList.length}
                  </Box>
               </Box>

               <Box
                  sx={{
                     display: 'flex',
                     gap: 2,
                     overflowX: 'auto',
                     pb: 1,
                     '&::-webkit-scrollbar': {
                        height: '6px',
                     },
                     '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '3px',
                     },
                     '&::-webkit-scrollbar-thumb': {
                        background: '#c1c1c1',
                        borderRadius: '3px',
                     },
                     '&::-webkit-scrollbar-thumb:hover': {
                        background: '#a8a8a8',
                     },
                  }}
               >
                  {/* Contact Waiting (QR Scan) Items */}
                  {contactWaitingList.map((contact, index) => (
                     <Box
                        key={`contact-${index}`}
                        sx={{
                           background: 'white',
                           borderRadius: '0.5rem',
                           p: 2,
                           border: '1px solid #f59e0b',
                           minWidth: '320px',
                           maxWidth: '320px',
                           flexShrink: 0,
                        }}
                     >
                        <Box
                           sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              mb: 1.5,
                           }}
                        >
                           <Avatar
                              src={contact.photoURL}
                              sx={{
                                 width: 40,
                                 height: 40,
                                 bgcolor: '#e5e7eb',
                                 color: '#6b7280',
                                 fontWeight: 600,
                              }}
                           >
                              {contact.displayName?.charAt(0).toUpperCase() || 'U'}
                           </Avatar>
                           <Box>
                              <Typography
                                 variant='body1'
                                 noWrap
                                 sx={{
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    mb: 0.25,
                                 }}
                              >
                                 {contact.displayName && contact.displayName.length > 20
                                    ? contact.displayName.substring(0, 20) + '...'
                                    : contact.displayName || 'Unknown'}
                              </Typography>
                              <Typography
                                 variant='body2'
                                 sx={{
                                    fontSize: '0.8rem',
                                    color: '#6b7280',
                                 }}
                              >
                                 {contact.email}
                              </Typography>
                           </Box>
                        </Box>

                        {/* Source Badge */}
                        <Box sx={{ mb: 1.5 }}>
                           <Chip
                              size='small'
                              label={
                                 contact.source === 'business_card'
                                    ? 'Business Card'
                                    : contact.source === 'profile_qr'
                                      ? 'Profile QR'
                                      : contact.source === 'downloadable_qr'
                                        ? 'Downloaded QR'
                                        : 'Sharing Link'
                              }
                              color='primary'
                              icon={<QrCodeIcon fontSize='small' />}
                              sx={{
                                 fontSize: '0.75rem',
                              }}
                           />
                        </Box>

                        {/* Timestamp */}
                        <Typography
                           variant='caption'
                           sx={{
                              color: '#9ca3af',
                              display: 'block',
                              mb: 1.5,
                              fontSize: '0.75rem',
                           }}
                        >
                           {contact.createdAt
                              ? `Signed up ${new Date(contact.createdAt).toLocaleDateString(
                                   'en-US',
                                   {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit',
                                   }
                                )}`
                              : 'Recently'}
                        </Typography>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                           <Button
                              fullWidth
                              variant='contained'
                              size='small'
                              startIcon={<CheckCircleIcon />}
                              disabled={processingWaitingId === contact.id}
                              onClick={() => handleApproveContact(contact.id)}
                              sx={{
                                 background: '#10b981',
                                 textTransform: 'none',
                                 '&:hover': {
                                    background: '#059669',
                                 },
                              }}
                           >
                              {processingWaitingId === contact.id ? 'Approving...' : 'Approve'}
                           </Button>
                           <Button
                              fullWidth
                              variant='outlined'
                              size='small'
                              startIcon={<Cancel />}
                              disabled={processingWaitingId === contact.id}
                              onClick={() => handleDeclineContact(contact.id)}
                              sx={{
                                 textTransform: 'none',
                                 borderColor: '#ef4444',
                                 color: '#ef4444',
                                 '&:hover': {
                                    borderColor: '#dc2626',
                                    background: '#fef2f2',
                                 },
                              }}
                           >
                              Decline
                           </Button>
                        </Box>
                     </Box>
                  ))}

                  {/* Pending Network Request Items */}
                  {networkRequests.filter(r => r.status === 'pending').map((request, index) => {
                     // Determine if this is an incoming or outgoing request
                     const isIncoming = request.toUserId === currentUser?.uid || request.toUserEmail === currentUser?.email;
                     const displayName = isIncoming ? request.fromUserName : (request.toUserName || request.toUserEmail.split('@')[0]);
                     const displayEmail = isIncoming ? request.fromUserEmail : request.toUserEmail;
                     const displayPhoto = isIncoming ? request.fromUserPhoto : request.toUserPhoto;
                     const direction = isIncoming ? 'Received' : 'Sent';
                     
                     console.log('🖼️ Rendering card:', {
                        direction,
                        displayName,
                        displayPhoto,
                        fromPhoto: request.fromUserPhoto,
                        toPhoto: request.toUserPhoto
                     });
                     
                     return (
                        <Box
                           key={request.id || index}
                           sx={{
                              background: 'white',
                              borderRadius: '0.5rem',
                              p: 2,
                              border: '1px solid #f59e0b',
                              minWidth: '320px',
                              maxWidth: '320px',
                              flexShrink: 0,
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: 1.5,
                                 mb: 1.5,
                              }}
                           >
                              <Avatar
                                 src={displayPhoto || undefined}
                                 sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: '#e5e7eb',
                                    color: '#6b7280',
                                    fontWeight: 600,
                                 }}
                              >
                                 {displayName?.charAt(0).toUpperCase() || displayEmail?.charAt(0).toUpperCase() || 'U'}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                 <Typography
                                    variant='body1'
                                    sx={{
                                       fontSize: '0.9rem',
                                       fontWeight: 600,
                                       mb: 0.25,
                                    }}
                                 >
                                    {isIncoming ? (
                                       <>
                                          <span style={{ color: '#01949A' }}>{displayName}</span>
                                          {' → You'}
                                       </>
                                    ) : (
                                       <>
                                          {'You → '}
                                          <span style={{ color: '#01949A' }}>{displayName}</span>
                                       </>
                                    )}
                                 </Typography>
                                 <Typography
                                    variant='body2'
                                    sx={{
                                       fontSize: '0.8rem',
                                       color: '#6b7280',
                                    }}
                                 >
                                    {displayEmail}
                                 </Typography>
                                 <Typography
                                    variant='caption'
                                    sx={{
                                       fontSize: '0.75rem',
                                       color: '#9ca3af',
                                    }}
                                 >
                                    {request.relationshipType ? relationshipLabels[request.relationshipType] : 'Friend'} • {direction}{' '}
                                    {request.createdAt
                                       ? new Date(request.createdAt).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                         })
                                       : 'recently'}
                                 </Typography>
                              </Box>
                           </Box>

                        {/* Source Badge */}
                        <Box sx={{ mb: 1.5 }}>
                           <Chip
                              size='small'
                              label={isIncoming ? 'Connection Request' : 'Sent Request'}
                              color={isIncoming ? 'info' : 'warning'}
                              icon={<PersonAddIcon fontSize='small' />}
                              sx={{
                                 fontSize: '0.75rem',
                              }}
                           />
                        </Box>

                        {/* Action Buttons - Different for incoming vs outgoing */}
                        {isIncoming ? (
                           // Incoming: Accept/Decline buttons
                           <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                 variant='contained'
                                 size='small'
                                 color='primary'
                                 onClick={() => handleAcceptRequest(request.id!)}
                                 disabled={processingRequestId === `accept-${request.id}` || processingRequestId === `decline-${request.id}`}
                                 startIcon={
                                    processingRequestId === `accept-${request.id}` ? (
                                       <CircularProgress size={16} color='inherit' />
                                    ) : undefined
                                 }
                                 sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    borderRadius: '0.375rem',
                                    flex: 1,
                                 }}
                              >
                                 {processingRequestId === `accept-${request.id}`
                                    ? 'Accepting...'
                                    : 'Accept'}
                              </Button>
                              <Button
                                 variant='outlined'
                                 size='small'
                                 color='error'
                                 onClick={() => handleDeclineRequest(request.id!)}
                                 disabled={processingRequestId === `accept-${request.id}` || processingRequestId === `decline-${request.id}`}
                                 startIcon={
                                    processingRequestId === `decline-${request.id}` ? (
                                       <CircularProgress size={16} color='inherit' />
                                    ) : undefined
                                 }
                                 sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    borderRadius: '0.375rem',
                                    flex: 1,
                                 }}
                              >
                                 {processingRequestId === `decline-${request.id}`
                                    ? 'Declining...'
                                    : 'Decline'}
                              </Button>
                           </Box>
                        ) : (
                           // Outgoing: Resend/Cancel buttons
                           <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                 variant='outlined'
                                 size='small'
                                 color='primary'
                                 onClick={() => handleResendNetworkRequest(request.id!)}
                                 disabled={processingRequestId === request.id}
                                 startIcon={
                                    processingRequestId === request.id ? (
                                       <CircularProgress size={16} color='inherit' />
                                    ) : (
                                       <SendIcon fontSize='small' />
                                    )
                                 }
                                 sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    borderRadius: '0.375rem',
                                    flex: 1,
                                 }}
                              >
                                 {processingRequestId === request.id ? 'Resending...' : 'Resend'}
                              </Button>
                              <Button
                                 variant='outlined'
                                 size='small'
                                 color='error'
                                 onClick={() => handleCancelRequest(request.id!)}
                                 disabled={processingRequestId === request.id}
                                 sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    borderRadius: '0.375rem',
                                    flex: 1,
                                 }}
                              >
                                 Cancel
                              </Button>
                           </Box>
                        )}
                     </Box>
                     );
                  })}
               </Box>
            </Paper>
         )}

         {success && (
            <Alert severity='success' sx={{ my: 2 }}>
               {success}
            </Alert>
         )}

         {renderContent()}

         {/* Edit relationship dialog */}
         {selectedContact && currentUser && (
            <EditRelationshipDialog
               open={editDialogOpen}
               onClose={() => setEditDialogOpen(false)}
               contact={selectedContact}
               currentUserId={currentUser.uid}
               onSaved={() => {
                  fetchContacts(); // Refresh contacts after editing
                  setSuccess('Relationship updated successfully!');
                  setTimeout(() => setSuccess(null), 3000); // Clear success message after 3 seconds
               }}
            />
         )}

         {/* Network invite modal */}
         <NetworkInviteModal
            open={inviteModalOpen}
            onClose={() => setInviteModalOpen(false)}
            onInviteSent={() => {
               fetchNetworkRequests(); // Refresh network requests after sending invitation
               fetchPendingInvitations(); // Refresh pending invitations after sending invitation
               setSuccess('Network invitation sent successfully!');
               setTimeout(() => setSuccess(null), 3000); // Clear success message after 3 seconds
            }}
         />

         {/* Profile Modal */}
         <ParticipantProfileModal
            open={profileModalOpen}
            onClose={handleCloseProfileModal}
            participant={selectedProfileContact}
         />

         {/* Virtual Call Scheduler Modal */}
         <VirtualCallSchedulerModal
            open={callSchedulerOpen}
            onClose={handleCloseCallScheduler}
            contact={selectedCallContact}
         />
      </Container>
   );
};

export default PeoplePage;
